<template>
  <!-- ── Icon mode ─────────────────────────────────────────────────────── -->
  <template v-if="mode === 'icon'">
    <div
      class="device-icon-pin"
      :class="[sizeClass, isOn ? 'device-active--switch' : '']"
      :style="{ '--pin-active-color': 'var(--device-color-switch)' }"
      :title="device.deviceName">
      <!-- The appliance drawing inside the status ring (P13); a `generic` kind
           draws the set's power glyph instead -->
      <ControlStatusRing :value="isOn ? 100 : 0">
        <SwitchAnimation
          v-if="hasApplianceArt"
          class="switch-art switch-art--pin"
          :class="{ 'switch-art--on': isOn }"
          :kind="switchKind"
          :active="isOn"
          :level="level" />
        <ControlIcon
          v-else
          name="power"
          :size="pinIconSize"
          :style="isOn ? { color: 'var(--device-color-switch)' } : {}" />
      </ControlStatusRing>
      <div v-if="isOn" class="device-icon-pin__glow"></div>
    </div>
  </template>

  <!-- ── Card mode ───────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'card'">
    <!-- Card face: the appliance's own drawing, its name and its state; the
         largest size carries the power verb. -->
    <DeviceCardShell
      :size="cardSize ?? 'min'"
      :name="device.deviceName"
      :title="device.deviceName"
      :active="isOn"
      :breathing="cardTone.breathing && isOn"
      :accent="cardTone.accent"
      class="switch-card-face"
      @click="handleToggle"
      @contextmenu.prevent="emit('open-control-modal')">
      <template #corner>
        <ApplianceRuntimeRing :runtime="runtime" :compact="cardSize === 'min'" :accent="cardTone.accent" />
      </template>

      <template #media>
        <!-- Raw <img> rather than DeviceThumbnail: that component is a fixed 2rem
             circle for the compact tile, which left the card's photo tiny. -->
        <img
          v-if="deviceThumbnailUrl"
          :src="deviceThumbnailUrl"
          alt=""
          class="switch-card-face__img"
          :class="{ 'switch-card-face__img--off': !isOn }"
          draggable="false" />
        <SwitchAnimation
          v-else-if="hasApplianceArt"
          class="switch-card-face__figure"
          :kind="switchKind"
          :active="isOn"
          :level="level" />
        <span v-else class="material-symbols-outlined switch-card-face__glyph">{{
          device.icon || 'power_settings_new'
        }}</span>
      </template>

      <template v-if="cardSize !== 'min'" #value>
        <span class="switch-card-face__value">{{ isOn ? tt('On', 'Vklopljeno') : tt('Off', 'Izklopljeno') }}</span>
      </template>

      <template v-if="cardFacts.length" #facts>
        <span v-for="fact in cardFacts" :key="fact.key" class="switch-card-face__fact" :title="fact.title">{{
          applianceFactText(fact)
        }}</span>
      </template>

      <template v-if="cardSize === 'max'" #control>
        <button
          type="button"
          class="switch-card-face__power"
          :class="{ 'switch-card-face__power--on': isOn }"
          :disabled="controlling"
          @click.stop="handleToggle">
          <span class="material-symbols-outlined">power_settings_new</span>
          {{ isOn ? tt('Turn off', 'Izklopi') : tt('Turn on', 'Vklopi') }}
        </button>
      </template>
    </DeviceCardShell>
  </template>

  <!-- ── Grid mode ───────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'grid'">
    <DeviceGridCell
      :icon="device.icon || 'power_settings_new'"
      :name="device.deviceName"
      :is-on="isOn"
      active-color="green-400"
      @toggle="setProperty(device, 'onOff', !isOn)"
      @long-press="emit('open-control-modal')" />
  </template>

  <!-- ── Control mode ──────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'control'">
    <!-- The body of the sheet: the host owns the head and the panel, the body
         owns the hero and the rows (no head, no outer padding — the host's body
         container carries it). The switch's own colour rides down while the load
         is live; otherwise the host's accent stands. -->
    <div class="switch-sheet" :style="isOn ? { '--sheet-accent': 'var(--device-color-switch)' } : {}">
      <!-- HERO — the appliance itself, drawn at sheet size (the same drawing the
           tile shows; a `generic` load has none), then the power dial under it.
           The dial is the round accent button: a ring dial would hard-code the
           bulb glyph and a 0–100 ring a switch cannot write. -->
      <div class="switch-hero">
        <div v-if="hasApplianceArt" class="switch-hero-figure" :class="{ 'switch-hero-figure--active': isOn }">
          <SwitchAnimation :kind="switchKind" :active="isOn" :level="level" />
        </div>
        <button
          type="button"
          class="switch-power"
          :class="{ 'switch-power--on': isOn, 'switch-power--busy': controlling }"
          :disabled="controlling"
          :aria-label="isOn ? tt('Turn off', 'Izklopi') : tt('Turn on', 'Vklopi')"
          @click="handleToggle">
          <span v-if="controlling" class="switch-power-spin" aria-hidden="true"></span>
          <ControlIcon v-else name="power" size="2.25rem" />
        </button>
        <div class="switch-hero-caption">{{ isOn ? tt('On', 'Vklopljeno') : tt('Off', 'Izklopljeno') }}</div>
      </div>

      <!-- ENERGY — the metered readings, only where the device reports them
           today (a socket's `power_w` / `voltage` / `current`, present only
           when its class carries metering); the demo sockets report none, so
           no row is drawn for them. -->
      <div v-if="energyTiles.length" class="switch-tiles">
        <ControlValueTile
          v-for="tile in energyTiles"
          :key="tile.key"
          :icon="tile.icon"
          :label="tile.label"
          :value="tile.value"
          :unit="tile.unit" />
      </div>

      <p v-if="lastUpdated" class="switch-meta">
        <ControlIcon name="clock" size="0.8125rem" />
        {{ tt('Last updated:', 'Zadnja posodobitev:') }} {{ lastUpdated }}
      </p>

      <SwitchAutomationPanel
        v-if="switchAutomations.length"
        :automations="switchAutomations"
        @updated="refreshSwitchAutomations" />

      <!-- Activity timeline -->
      <SetpointTimeline
        :device-values="device.deviceValues ?? []"
        :device-id="device.id"
        accent-color="rgb(34, 197, 94)" />
    </div>
  </template>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import { formatTime } from '../../utils/formatDate';
import { useDeviceValues } from '../../composables/useDeviceValues';
import { useApplianceRuntime } from '../../composables/useApplianceRuntime';
import { useDeviceControl } from '../../composables/useDeviceControl';
import { useSolar } from '../../composables/useSolar';
import DeviceGridCell from '../panel/DeviceGridCell.vue';
import { getDefaultThumbnailUrl } from '../../composables/useDeviceThumbnail';
import SetpointTimeline from './SetpointTimeline.vue';
import ApplianceRuntimeRing from './ApplianceRuntimeRing.vue';
import { applianceCardTone } from '../../utils/applianceCardTone';
import { applianceFactText, applianceFacts } from '../../utils/applianceFacts';
import DeviceCardShell from './DeviceCardShell.vue';
import SwitchAnimation from './switch/SwitchAnimation.vue';
import ControlValueTile from '../device-sheet/ControlValueTile.vue';
import ControlIcon from '../device-icons/ControlIcon.vue';
import ControlStatusRing from '../device-sheet/ControlStatusRing.vue';
import { switchKindOf, type SwitchKind } from './deviceWidgetTypes';
import SwitchAutomationPanel from './SwitchAutomationPanel.vue';
import { useDeviceAutomationsStore } from '../../stores/deviceAutomations';
import { tt } from '../../utils/consoleText';
import {
  buildSwitchAutomationDisplay,
  getSwitchWidgetAutomationSummaries,
  toSunTimesLike,
  type SwitchAutomationDisplay,
} from '../../utils/switchAutomation';
import type { Device } from '@/types';

interface Props {
  device: Device;
  mode?: 'icon' | 'card' | 'control' | 'grid';
  size?: 'sm' | 'md' | 'lg';
  fluid?: boolean;
  displayMode?: 'full' | 'room';
  /** Set by the pages that grid cards (`DeviceButton`) — `undefined` keeps the
   *  compact button row other surfaces have always drawn. */
  cardSize?: 'min' | 'mid' | 'max';
}

const props = withDefaults(defineProps<Props>(), { mode: 'card', size: 'md', cardSize: undefined });

const emit = defineEmits<{
  toggle: [];
  'open-control-modal': [];
  'view-stream': [];
  'view-details': [];
  'value-update': [valueId: string, newValue: unknown];
}>();

// ── State ────────────────────────────────────────────────────────────────
const { currentValues, initializeValues } = useDeviceValues(props.device.id);
// The card's corner reads the load's remaining time from the same live values
// this widget already keeps, so a plug with a timer does not open a second
// subscription just to show its countdown.
const { runtime } = useApplianceRuntime(props.device.id, undefined, currentValues);

/** What this load *is* — a coffee machine plug is a coffee machine, a hob is
 *  red — and whether it breathes while it works. */
const cardTone = computed(() => applianceCardTone(props.device));

/** The readings beside the state: power draw, a temperature, a cycle figure.
 *  A mid card is narrow, so it carries one of them and a max card two. */
const cardFacts = computed(() =>
  applianceFacts(props.device.deviceValues, { limit: props.cardSize === 'mid' ? 1 : 2 }),
);
const { controlling, setProperty } = useDeviceControl();
const automationsStore = useDeviceAutomationsStore();
const solar = useSolar();
const nowTick = ref(Date.now());

let nowInterval: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
  nowInterval = setInterval(advanceAutomationClock, 1000);

  void automationsStore.fetchDeviceAutomationSummaries(props.device.id);
});

onUnmounted(() => {
  if (nowInterval) {
    clearInterval(nowInterval);
    nowInterval = null;
  }
});

if (props.device.deviceValues?.length) initializeValues(props.device.deviceValues);
watch(
  () => props.device.deviceValues,
  (v) => {
    if (v?.length) initializeValues(v);
  },
  { deep: true },
);

const isOn = computed(() => {
  const key = ['power', 'on_off', 'state', 'enabled'].find((k) => k in currentValues.value);
  if (key) {
    const v = currentValues.value[key];
    return v === true || v === 1 || v === 'on';
  }
  const dv = props.device.deviceValues?.find((v) => ['power', 'on_off', 'state', 'enabled'].includes(v.valueKey));
  return dv?.currentValue === true || dv?.currentValue === 1 || dv?.currentValue === 'on';
});

// The card's background picture: the device's own thumbnail, resolved cloud-safely.
const deviceThumbnailUrl = computed(() => getDefaultThumbnailUrl(props.device));

const lastUpdated = computed(() => (Object.keys(currentValues.value).length > 0 ? formatTime(new Date()) : ''));

async function handleToggle() {
  await setProperty(props.device, 'onOff', !isOn.value);
}

async function refreshSwitchAutomations() {
  automationsStore.invalidate(props.device.id);
  await Promise.all([
    automationsStore.fetchDeviceAutomationSummaries(props.device.id, true),
    automationsStore.fetchDeviceAutomations(props.device.id, true),
  ]);
}
const sizeClass = computed(() => (props.fluid ? 'device-icon-pin--fluid' : `device-icon-pin--${props.size ?? 'md'}`));

/** The drawn fallback glyph's size inside the pin (P13), in the `GenericIcon` steps. */
const pinIconSize = computed(() => {
  if (props.fluid) return '28px';
  switch (props.size) {
    case 'lg':
      return '28px';
    case 'sm':
      return '16px';
    default:
      return '22px';
  }
});

// ── The appliance drawing ────────────────────────────────────────────────
/** `settings.device_name` — the socket class's own Connected Load Name. */
const connectedLoadName = computed<string | null>(() => {
  const settings = props.device.settings;
  if (typeof settings !== 'object' || settings === null) return null;
  const value = (settings as Record<string, unknown>).device_name;
  return typeof value === 'string' ? value : null;
});

const switchKind = computed<SwitchKind>(() =>
  switchKindOf({
    icon: props.device.icon,
    deviceType: props.device.deviceType,
    controlType: props.device.deviceControl || props.device.controlType,
    deviceName: props.device.deviceName,
    shortName: props.device.shortName,
    settings: props.device.settings,
    connectedLoadName: connectedLoadName.value,
  }),
);

/** `generic` means "no drawing" — the tile keeps the device's own glyph. */
const hasApplianceArt = computed(() => switchKind.value !== 'generic');

/** A dimmable plug's level, for the bulb's glow. */
const level = computed<number | null>(() => {
  const raw =
    currentValues.value['level'] ?? props.device.deviceValues?.find((v) => v.valueKey === 'level')?.currentValue;
  const numeric = Number(raw);
  return raw === null || raw === undefined || raw === '' || !Number.isFinite(numeric) ? null : numeric;
});

/**
 * The metered readings this device reports today — a socket's `power_w` (W),
 * `voltage` (V) and `current` (A). Those value rows exist only when the class
 * carries metering, so the demo sockets (metering `none`) render no energy row
 * at all; the sheet never invents a reading. The icon fold follows
 * `SensorWidget`'s: every electrical reading draws the bolt.
 */
const energyTiles = computed<Array<{ key: string; icon: string; label: string; value: string; unit: string }>>(() => {
  const defs = [
    { key: 'power_w', icon: 'bolt', label: tt('Power', 'Moč'), unit: 'W' },
    { key: 'voltage', icon: 'bolt', label: tt('Voltage', 'Napetost'), unit: 'V' },
    { key: 'current', icon: 'bolt', label: tt('Current', 'Tok'), unit: 'A' },
  ];
  const tiles: Array<{ key: string; icon: string; label: string; value: string; unit: string }> = [];
  for (const def of defs) {
    const raw =
      currentValues.value[def.key] ?? props.device.deviceValues?.find((v) => v.valueKey === def.key)?.currentValue;
    if (raw === null || raw === undefined || raw === '') continue;
    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) continue;
    tiles.push({ ...def, value: String(Math.round(numeric * 100) / 100) });
  }
  return tiles;
});

const switchAutomations = computed<SwitchAutomationDisplay[]>(() => {
  const summaries = getSwitchWidgetAutomationSummaries(automationsStore.getDeviceAutomationSummaries(props.device.id));

  return summaries
    .map((automation) =>
      buildSwitchAutomationDisplay(
        automation,
        currentValues.value,
        new Date(nowTick.value),
        toSunTimesLike(solar.sunTimes.value),
      ),
    )
    .filter((automation): automation is SwitchAutomationDisplay => automation !== null);
});

/**
 * `nowTick` exists for two labels only: a running countdown (which prints
 * seconds) and a schedule label (which prints minutes). Writing it every second
 * re-rendered the whole card once a second for text that could not have changed;
 * the tick now fires only when one of those two grains has actually passed.
 */
function advanceAutomationClock(): void {
  const now = Date.now();
  const minutePassed = Math.floor(now / 60_000) !== Math.floor(nowTick.value / 60_000);
  const countingDown = switchAutomations.value.some((automation) => automation.countdownText !== '');
  if (minutePassed || countingDown) nowTick.value = now;
}
</script>

<style scoped>
.active .device-name,
.active .device-current-value {
  color: var(--device-color-switch) !important;
}
.active {
  border-color: var(--device-color-switch) !important;
  background-color: transparent !important;
}

/* Device photo fills the icon slot as a circle */
.device-icon :deep(.device-thumb) {
  width: 100%;
  height: 100%;
}

/* ── The appliance drawing ───────────────────────────────────────────────
   `currentColor` is what the drawing paints itself with, so the off state
   takes the tile's own muted ink and the live state takes the switch colour
   — the same pairing the tile's name and status line already use. */
.switch-art {
  width: 100%;
  height: 100%;
  color: color-mix(in srgb, currentColor 78%, transparent);
  transition: color 0.3s ease;
}

.switch-art--on {
  color: var(--device-color-switch);
}

/* Inside the icon pin the drawing sits at the glyph's own ratio */
.switch-art--pin {
  width: 62%;
  height: 62%;
}

/* ── Control mode: the sheet body ────────────────────────────────────────
   The appliance drawing hero at sheet size, the power dial under it, the
   energy tiles (only when the device reports metering) and the meta line.
   Every accent reads `--sheet-accent`, which the body root hands down while
   the load is live; otherwise the host's accent stands. */
.switch-sheet {
  display: flex;
  flex-direction: column;
  gap: calc(1rem * var(--ui-space-factor));
  min-width: 0;
}

.switch-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding-top: 0.25rem;
}

.switch-hero-figure {
  width: 9rem;
  height: 9rem;
  color: rgb(148, 163, 184);
  transition: color 0.3s ease;
}

.dark .switch-hero-figure {
  color: rgb(100, 116, 139);
}

.switch-hero-figure--active {
  color: var(--device-color-switch);
}

/* The power dial: a 6rem circle, tinted by the sheet's accent while the load
   is live; the busy state calms it. */
.switch-power {
  width: 6rem;
  height: 6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--glass-edge);
  border-radius: 50%;
  background: rgba(148, 163, 184, 0.1);
  color: rgb(var(--gray-500));
  cursor: pointer;
  transition:
    color 0.2s,
    border-color 0.2s,
    background 0.2s,
    box-shadow 0.2s,
    transform 0.15s;
}

.dark .switch-power {
  background: rgba(255, 255, 255, 0.04);
  color: rgb(var(--gray-400));
}

.switch-power:hover:not(:disabled) {
  transform: translateY(-1px);
}

.switch-power--on {
  border-color: color-mix(in srgb, var(--sheet-accent) 55%, transparent);
  background: color-mix(in srgb, var(--sheet-accent) 13%, transparent);
  color: var(--sheet-accent);
  box-shadow: 0 0 24px color-mix(in srgb, var(--sheet-accent) 22%, transparent);
}

.switch-power--busy {
  opacity: 0.7;
}

.switch-power:disabled {
  cursor: default;
}

.switch-power-spin {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  border: 2px solid color-mix(in srgb, var(--sheet-accent) 35%, transparent);
  border-top-color: var(--sheet-accent);
  animation: switch-sheet-spin 0.8s linear infinite;
}

@keyframes switch-sheet-spin {
  to {
    transform: rotate(360deg);
  }
}

/* The energy tiles — only the readings the device reports (socket metering). */
.switch-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(9.375rem, 1fr));
  gap: calc(0.5rem * var(--ui-space-factor));
}

.switch-meta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35em;
  margin: 0;
  font-size: 0.6875rem;
  color: rgb(var(--gray-500));
}

.dark .switch-meta {
  color: rgb(var(--gray-400));
}

@media (prefers-reduced-motion: reduce) {
  .switch-power-spin {
    animation: none !important;
  }
}

/* ── Card face ───────────────────────────────────────────────────────────────
   The shell owns the frame, the name and the size rules; these are the device's
   own parts inside it. */
.switch-card-face__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition:
    filter 0.3s ease,
    opacity 0.3s ease;
}

/* Off: the picture is there, just unlit — the same grayscale the binary
   sensor's clear face draws, so one "off" reads the same on every card. */
.switch-card-face__img--off {
  filter: grayscale(1);
  opacity: 0.72;
}

.switch-card-face__glyph {
  font-size: 2.4rem;
}

.switch-card-face__value {
  flex: none;
  text-align: center;
  font-size: var(--card-value, 0.85rem);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--card-ink, #64748b);
}

.switch-card-face__fact {
  overflow: hidden;
  padding: 0 0.3rem;
  border-radius: 9999px;
  background: color-mix(in srgb, currentColor 14%, transparent);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.switch-card-face__figure {
  width: 100%;
  height: 100%;
}

.switch-card-face__power {
  display: inline-flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  border-radius: 0.5rem;
  background: rgba(148, 163, 184, 0.16);
  padding: 0.3rem 0.4rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: #475569;
}

.switch-card-face__power--on {
  background: color-mix(in srgb, var(--card-accent, rgb(250 204 21)) 26%, transparent);
  color: color-mix(in srgb, var(--card-accent, rgb(250 204 21)) 55%, #111827);
}

.switch-card-face__power .material-symbols-outlined {
  font-size: 1rem;
}

.dark .switch-card-face__power {
  background: rgba(255, 255, 255, 0.08);
  color: #cbd5e1;
}

.dark .switch-card-face__power--on {
  background: color-mix(in srgb, var(--card-accent, rgb(250 204 21)) 22%, transparent);
  color: color-mix(in srgb, var(--card-accent, rgb(250 204 21)) 45%, #f8fafc);
}

.switch-card-face__figure :deep(svg),
.switch-card-face__figure svg {
  width: 100%;
  height: 100%;
}
</style>
